import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Service } from 'src/app/core/jsonapi/service';
import {
    ApiUsageDailyPoint,
    ApiUsageEndpoint,
    ApiUsageHourlySeries,
    ApiUsageHourOfDay,
    ApiUsageLogFilters,
    ApiUsageLogPage,
    ApiUsageLogResponse,
    ApiUsageResource,
    ApiUsageStatusFilter,
    ApiUsageStatusSlice,
    ApiUsageSummary
} from '../_types/api-usage';

/// Consultas del panel de uso de la API. Ninguno de estos endpoints es JSON:API: se llaman con this.http
/// heredado y devuelven JSON normal. Todos pasan por loaderService.skipRequest para que el spinner global no
/// se dispare con las cuatro peticiones que hace el panel al cargar.
///
/// Todas llevan el filtro de código de respuesta, que en el panel es general de la pantalla. La excepción es
/// getStatusCodes: es el selector de ese filtro, así que se pide siempre sin filtrar.
@Injectable({
    providedIn: 'root'
})
export class ApiUsageService extends Service<ApiUsageResource> {
    public override type = 'apiUsage';
    public override url = 'auth/apiUsage';

    /// Contadores del periodo para la fila de indicadores.
    public getSummary(userId: string, dateFrom: Date, dateTo: Date, status?: ApiUsageStatusFilter): Observable<ApiUsageSummary> {
        const url = this.getAPIUrl() + '/summary' + this.buildRangeQuery(userId, dateFrom, dateTo) + this.buildStatusQuery(status);
        this.loaderService.skipRequest(url);
        return this.http.get<ApiUsageSummary>(url);
    }

    /// Peticiones, errores y duración media por día UTC.
    public getDaily(userId: string, dateFrom: Date, dateTo: Date, status?: ApiUsageStatusFilter): Observable<ApiUsageDailyPoint[]> {
        const url = this.getAPIUrl() + '/daily' + this.buildRangeQuery(userId, dateFrom, dateTo) + this.buildStatusQuery(status);
        this.loaderService.skipRequest(url);
        return this.http.get<ApiUsageDailyPoint[]>(url);
    }

    /// Peticiones, errores y duración media por hora UTC del rango. El servidor la limita a 31 días y por
    /// encima responde source 'unavailable' sin puntos.
    public getHourlySeries(userId: string, dateFrom: Date, dateTo: Date, status?: ApiUsageStatusFilter): Observable<ApiUsageHourlySeries> {
        const url = this.getAPIUrl() + '/hourlySeries' + this.buildRangeQuery(userId, dateFrom, dateTo) + this.buildStatusQuery(status);
        this.loaderService.skipRequest(url);
        return this.http.get<ApiUsageHourlySeries>(url);
    }

    /// Perfil por hora del día: 24 puntos con lo acumulado del periodo.
    public getHourOfDay(userId: string, dateFrom: Date, dateTo: Date, status?: ApiUsageStatusFilter): Observable<ApiUsageHourOfDay> {
        const url = this.getAPIUrl() + '/hourOfDay' + this.buildRangeQuery(userId, dateFrom, dateTo) + this.buildStatusQuery(status);
        this.loaderService.skipRequest(url);
        return this.http.get<ApiUsageHourOfDay>(url);
    }

    /// Reparto por código de estado del periodo, siempre sin filtrar: es lo que alimenta el selector del
    /// filtro de código, y filtrado se quedaría en una sola porción sin manera de volver atrás.
    public getStatusCodes(userId: string, dateFrom: Date, dateTo: Date): Observable<ApiUsageStatusSlice[]> {
        const url = this.getAPIUrl() + '/statusCodes' + this.buildRangeQuery(userId, dateFrom, dateTo);
        this.loaderService.skipRequest(url);
        return this.http.get<ApiUsageStatusSlice[]>(url);
    }

    /// Endpoints más llamados, agrupados por método y plantilla de ruta.
    public getTopEndpoints(userId: string, dateFrom: Date, dateTo: Date, top: number = 10,
        status?: ApiUsageStatusFilter): Observable<ApiUsageEndpoint[]> {

        const url = this.getAPIUrl() + '/topEndpoints' + this.buildRangeQuery(userId, dateFrom, dateTo)
            + '&top=' + top + this.buildStatusQuery(status);
        this.loaderService.skipRequest(url);
        return this.http.get<ApiUsageEndpoint[]>(url);
    }

    /// Página del registro de peticiones. El backend espera número de página, no índice de registro.
    public getLog(userId: string, dateFrom: Date, dateTo: Date, page: number, pageSize: number,
        filters: ApiUsageLogFilters, sort: string, status?: ApiUsageStatusFilter): Observable<ApiUsageLogPage> {

        let url = this.getAPIUrl() + '/log' + this.buildRangeQuery(userId, dateFrom, dateTo) + this.buildStatusQuery(status);
        url += '&page=' + page + '&pageSize=' + pageSize + '&sort=' + encodeURIComponent(sort);

        if (filters.method) url += '&method=' + encodeURIComponent(filters.method);
        if (filters.routeTemplate) url += '&routeTemplate=' + encodeURIComponent(filters.routeTemplate);
        if (filters.minDurationMs) url += '&minDurationMs=' + filters.minDurationMs;

        this.loaderService.skipRequest(url);
        return this.http.get<ApiUsageLogPage>(url);
    }

    /// Respuesta guardada de una petición del registro. El rango viaja además del identificador porque es la
    /// clave por la que el servidor puede encontrar la fila con una búsqueda por índice.
    public getLogResponse(userId: string, dateFrom: Date, dateTo: Date, id: string): Observable<ApiUsageLogResponse> {
        const url = this.getAPIUrl() + '/log/response' + this.buildRangeQuery(userId, dateFrom, dateTo) + '&id=' + id;
        this.loaderService.skipRequest(url);
        return this.http.get<ApiUsageLogResponse>(url);
    }

    private buildRangeQuery(userId: string, dateFrom: Date, dateTo: Date): string {
        return '?userId=' + userId + '&dateFrom=' + dateFrom.toISOString() + '&dateTo=' + dateTo.toISOString();
    }

    /// Filtro de código. Se compara contra null y no por veracidad: el 0 es un código con significado
    /// propio, la petición que el cliente abortó antes de recibir la respuesta.
    private buildStatusQuery(status?: ApiUsageStatusFilter | null): string {
        if (status == null) return '';

        let query = '';
        if (status.statusCode != null) query += '&statusCode=' + status.statusCode;
        if (status.statusCodeClass != null) query += '&statusCodeClass=' + status.statusCodeClass;
        return query;
    }
}
